/**
 * Clown Image Toggle (v2) - updated for new markup
 *
 * Markup assumed:
 * - Container: div[data-testid="section-content"] (aka .section-content-wrapper)
 * - First .image-block  => STILL image
 * - Second .image-block => WINK gif
 *
 * Behavior:
 * - Desktop: show wink on hover, then auto-return to still after GIF_DURATION.
 * - Mobile: tap to play wink once, then auto-return to still after GIF_DURATION.
 *
 * Notes:
 * - We can't reliably detect the end of a GIF, so GIF_DURATION is an estimate.
 * - To avoid "double wink", we blank the wink img src before setting a cache-busted src.
 */

(function () {
  'use strict';

  const CONTAINER_SELECTOR = 'div[data-testid="section-content"]';
  const GIF_DURATION = 1500; // ms – adjust to match the wink gif

  let initialized = false;

  const DEBUG =
    typeof window !== 'undefined' && Boolean(window.CLOWN_TOGGLE_DEBUG);

  function log(...args) {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log('[clown-toggle-v2]', ...args);
  }

  function getNodesForContainer(container) {
    const blocks = container.querySelectorAll('.image-block');
    if (blocks.length < 2) return null;

    // Prefer explicit matching by filename (markup often has extra .image-blocks)
    let stillBlock = null;
    let winkBlock = null;
    let winkImg = null;

    for (const block of blocks) {
      const img = block.querySelector('img');
      if (!img) continue;

      const src =
        img.getAttribute('src') || img.currentSrc || img.src || '';
      if (/wink/i.test(src)) {
        winkBlock = block;
        winkImg = img;
      } else if (/still/i.test(src)) {
        stillBlock = block;
      }
    }

    // Fallback: assume first = still, second = wink
    if (!stillBlock || !winkBlock || !winkImg) {
      stillBlock = stillBlock || blocks[0];
      winkBlock = winkBlock || blocks[1];
      winkImg = winkImg || winkBlock.querySelector('img');
      if (!winkImg) return null;
    }

    const originalSrc = winkImg.getAttribute('src') || winkImg.src;
    const baseSrc = originalSrc.split('?')[0].split('#')[0];

    return { container, stillBlock, winkBlock, winkImg, baseSrc };
  }

  function findNodes() {
    const containers = document.querySelectorAll(CONTAINER_SELECTOR);
    if (!containers.length) return null;

    // Pick the first container that actually contains a wink image
    for (const container of containers) {
      const nodes = getNodesForContainer(container);
      if (!nodes) continue;

      const winkSrc =
        nodes.winkImg.getAttribute('src') || nodes.winkImg.currentSrc || '';
      if (/wink/i.test(winkSrc)) return nodes;
    }

    // Otherwise, just use the first container with >=2 blocks
    return getNodesForContainer(containers[0]) || null;
  }

  // Hide wink ASAP to prevent load flicker
  (function hideWinkImmediately() {
    const nodes = findNodes();
    if (nodes) {
      nodes.winkBlock.style.display = 'none';
      nodes.stillBlock.style.display = 'block';
      return;
    }
    if (document.readyState === 'loading') {
      requestAnimationFrame(hideWinkImmediately);
    }
  })();

  function init() {
    if (initialized) return;

    const nodes = findNodes();
    if (!nodes) {
      log('No matching container/nodes found for', CONTAINER_SELECTOR);
      return;
    }

    const { container, stillBlock, winkBlock, winkImg, baseSrc } = nodes;
    log('Initialized on container', container);

    // Initial state
    winkBlock.style.display = 'none';
    stillBlock.style.display = 'block';

    const isTouchDevice =
      'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;

    let autoHideTimeout = null;
    let lastTapTime = 0;
    let isPlaying = false;

    function clearAutoHide() {
      if (autoHideTimeout) {
        clearTimeout(autoHideTimeout);
        autoHideTimeout = null;
      }
    }

    function showStill() {
      clearAutoHide();
      isPlaying = false;
      winkBlock.style.display = 'none';
      stillBlock.style.display = 'block';
    }

    function playWinkOnce() {
      // prevent re-entrant plays (helps with double-fires)
      if (isPlaying) return;
      isPlaying = true;

      // show wink
      stillBlock.style.display = 'none';
      winkBlock.style.display = 'block';

      clearAutoHide();

      // blank src so it can't "start playing", then restart with cache-bust
      winkImg.onload = null;
      winkImg.removeAttribute('src');
      winkImg.src = '';

      const separator = baseSrc.includes('?') ? '&' : '?';
      const cacheBustSrc = baseSrc + separator + 't=' + Date.now();

      winkImg.onload = () => {
        clearAutoHide();
        autoHideTimeout = setTimeout(showStill, GIF_DURATION);
      };

      requestAnimationFrame(() => {
        winkImg.src = cacheBustSrc;
        winkImg.setAttribute('src', cacheBustSrc);
      });
    }

    if (isTouchDevice) {
      container.style.cursor = 'pointer';

      container.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // ignore rapid double-taps/clicks
          const now = Date.now();
          if (now - lastTapTime < 300) return;
          lastTapTime = now;

          // Always "play once" on tap (no toggling state needed)
          playWinkOnce();
        },
        { passive: false }
      );
    } else {
      container.addEventListener('mouseenter', () => {
        playWinkOnce();
      });

      container.addEventListener('mouseleave', () => {
        showStill();
      });
    }

    initialized = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

